import React from 'react';
import { Box, BoxProps } from '@mui/material';
import { useScrollReveal } from '../../hooks/useScrollReveal';

interface ScrollRevealProps extends Omit<BoxProps, 'ref'> {
  /** Delay before the reveal animation starts (ms). */
  delay?: number;
  /** How many pixels the element slides up from on reveal. */
  distance?: number;
}

/**
 * Wrapper that fades and slides its children into view when they enter the
 * viewport. On desktop the element is already visible so no animation plays.
 */
const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  delay = 0,
  distance = 28,
  sx,
  ...rest
}) => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();

  return (
    <Box
      ref={ref}
      {...rest}
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : `translateY(${distance}px)`,
        transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
        /* On desktop everything is already in view — skip the animation */
        '@media (min-width: 900px)': {
          opacity: 1,
          transform: 'none',
          transition: 'none',
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default ScrollReveal;
